import { Link } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import ConfirmModal from "components/common/ConfirmModal";
import { MdMoreVert, MdVisibility, MdEdit, MdDelete } from "react-icons/md";

export default function OrganizerTable({
  data,
  onDelete,
  onRefresh,
  selectedRows = [],
  onSelectRow,
  onSelectAll,
  viewButton,
  role = "admin",
}) {
  const [deleteId, setDeleteId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdownId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggleMenu = (e, rowId) => {
    e.stopPropagation();
    if (openDropdownId === rowId) {
      setOpenDropdownId(null);
    } else {
      // Check distance from bottom of screen to flip upward if space is tight
      const buttonRect = e.currentTarget.getBoundingClientRect();
      const spaceBelow = window.innerHeight - buttonRect.bottom;

      // If less than 150px remaining below button, open UPWARDS
      if (spaceBelow < 150) {
        setOpenUpward(true);
      } else {
        setOpenUpward(false);
      }

      setOpenDropdownId(rowId);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      await onDelete(deleteId);
      setDeleteId(null);
      onRefresh();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getEventStatus = (
    eventStartDate,
    eventEndDate,
    ticketStartDate,
    ticketEndDate
  ) => {
    const now = new Date();

    // Parse Dates safely
    const eStart = eventStartDate ? new Date(eventStartDate) : null;
    const eEnd = eventEndDate ? new Date(eventEndDate) : eStart;

    const tStart = ticketStartDate ? new Date(ticketStartDate) : null;
    const tEnd = ticketEndDate ? new Date(ticketEndDate) : tStart;

    // 1. Check if ACTIVE (Ticket sales are currently live OR event is currently happening)
    const isTicketActive = tStart && tEnd && tStart <= now && now <= tEnd;
    const isEventActive = eStart && eEnd && eStart <= now && now <= eEnd;
    // console.log("isTicketActive:", isTicketActive, "isEventActive:", isEventActive);

    if (isTicketActive || isEventActive) {
      return {
        label: "Active",
        class: "bg-[#dcfce7] text-[#16a34a]", // Light Green
      };
    }

    // 2. Check if UPCOMING (Everything is scheduled for the future)
    const isTicketUpcoming = tStart && now < tStart;
    const isEventUpcoming = eStart && now < eStart;

    if (isTicketUpcoming || isEventUpcoming) {
      return {
        label: "Upcoming",
        class: "bg-[#e0f2fe] text-[#0284c7]", // Light Blue
      };
    }

    // 3. Default fallback: COMPLETED (All dates have passed)
    return {
      label: "Completed",
      class: "bg-[#f1f5f9] text-[#64748b]", // Light Gray
    };
  };

  const formatEventDates = (startDateStr, endDateStr) => {
    if (!startDateStr) return "N/A";

    // Taariikha irraa kutaa "YYYY-MM-DD" qofa adda baasuu (Sa'aatii malee)
    const getDateOnly = (dateStr) => {
      if (!dateStr) return null;
      const parts = dateStr.split("T")[0].split("-");
      return {
        year: parseInt(parts[0], 10),
        month: parseInt(parts[1], 10) - 1, // Month is 0-indexed in JS
        day: parseInt(parts[2], 10),
      };
    };

    const start = getDateOnly(startDateStr);
    const end = getDateOnly(endDateStr);

    if (!start) return "N/A";

    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const startMonth = months[start.month];

    // Yoo guyyaan dhumaa hin jiru ta'e ykn guyyaa tokko qofa ta'e
    if (
      !end ||
      (start.year === end.year &&
        start.month === end.month &&
        start.day === end.day)
    ) {
      return `${start.day} ${startMonth} ${start.year}`;
    }

    const endMonth = months[end.month];

    // Haala 1: Ji'a fi waggaa walfakkaatu (fkn: "27-28 Oct 2026")
    if (start.month === end.month && start.year === end.year) {
      return `${start.day}-${end.day} ${endMonth} ${end.year}`;
    }

    // Haala 2: Ji'a adda addaa, waggaa walfakkaatu (fkn: "27 Oct - 02 Nov 2026")
    if (start.year === end.year) {
      return `${start.day} ${startMonth} - ${end.day} ${endMonth} ${end.year}`;
    }

    // Haala 3: Waggaa adda addaa (fkn: "28 Dec 2026 - 02 Jan 2027")
    return `${start.day} ${startMonth} ${start.year} - ${end.day} ${endMonth} ${end.year}`;
  };

  return (
    <>
      <div className="table-scroll-body">
        <table className="custom-table">
          <thead className="custom-table-header">
            <tr>
              {role !== "company" && (
                <th className="w-12 text-center">
                  <input
                    type="checkbox"
                    className="table-checkbox"
                    onChange={onSelectAll}
                    checked={
                      data.length > 0 && selectedRows.length === data.length
                    }
                  />
                </th>
              )}
              <th>Event Name</th>
              <th>Contact</th>
              <th>Event Date</th>
              <th>Location</th>
              <th className="text-center">Ticket Sold</th>
              <th className="text-center">Status</th>
              <th className="w-16 text-center"></th>
            </tr>
          </thead>

          <tbody>
            {data.length === 0 ? (
              <tr>
                <td
                  colSpan="8"
                  className="custom-table-cell py-12 text-center text-gray-500"
                >
                  No companies found
                </td>
              </tr>
            ) : (
              data.map((item, index) => {
                const rowId = item._id || index;
                const isChecked = selectedRows.includes(rowId);
                const statusInfo = getEventStatus(
                  item.ticketStartDate,
                  item.ticketEndDate,
                  item.eventStartDate,
                  item.eventEndDate
                );
                const isMenuOpen = openDropdownId === rowId;

                return (
                  <tr key={rowId} className="custom-table-row">
                    {role !== "company" && (
                      <td className="custom-table-cell text-center">
                        <input
                          type="checkbox"
                          className="table-checkbox"
                          checked={isChecked}
                          onChange={() => onSelectRow(rowId)}
                        />
                      </td>
                    )}
                    <td className="custom-table-cell font-bold text-gray-800">
                      {item.companyName || "N/A"}
                    </td>

                    <td className="custom-table-cell col-contact">
                      <div className="flex flex-col">
                        <span className="font-medium ">
                          {item.email || item.companyEmail || "N/A"}
                        </span>
                        <span className="text-xs ">
                          {item.companyMobileCountryCode
                            ? `${item.companyMobileCountryCode} ${item.companyMobile}`
                            : ""}
                        </span>
                      </div>
                    </td>

                    <td className="custom-table-cell ">
                      {formatEventDates(item.eventStartDate, item.eventEndDate)}
                    </td>

                    <td className="custom-table-cell col-contact ">
                      {item.companyAddress ? `${item.companyAddress}` : "N/A"}
                    </td>

                    <td className="custom-table-cell text-center ">
                      {item.ticketsSold !== undefined ? item.ticketsSold : "-"}
                    </td>

                    <td className="custom-table-cell text-center">
                      <span
                        className={`inline-block rounded-[8px] px-3 py-1 text-xs font-semibold ${statusInfo.class}`}
                      >
                        {statusInfo.label}
                      </span>
                    </td>

                    {/* Action Dropdown Cell */}
                    <td className="custom-table-cell relative text-center">
                      <button
                        onClick={(e) => handleToggleMenu(e, rowId)}
                        className="action-menu-btn"
                      >
                        <MdMoreVert className="text-lg" />
                      </button>

                      {/* Smart Directional Dropdown */}
                      {isMenuOpen && (
                        <div
                          ref={dropdownRef}
                          className={`action-dropdown-menu ${
                            openUpward ? "menu-bottom" : "menu-top"
                          }`}
                        >
                          <button
                            onClick={() => viewButton(item)}
                            className="action-dropdown-item"
                          >
                            <MdVisibility className="text-base" />
                            View
                          </button>
                          <div className="action-dropdown-divider" />
                          <Link
                            to={`/admin/companies/edit/${item._id}`}
                            onClick={() => setOpenDropdownId(null)}
                            className="action-dropdown-item"
                          >
                            <MdEdit className="text-base" />
                            Edit
                          </Link>
                          <div className="action-dropdown-divider" />

                          <button
                            onClick={() => {
                              setDeleteId(item._id);
                              setOpenDropdownId(null);
                            }}
                            className="action-dropdown-item"
                          >
                            <MdDelete className="text-base" />
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <ConfirmModal
        open={Boolean(deleteId)}
        title="Delete Organizer"
        message="This action cannot be undone. Do you want to continue?"
        confirmText="Delete"
        loading={loading}
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />
    </>
  );
}
